import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Info } from 'lucide-react';
import ReportWidget from './components/ReportWidget';
import { DataPoint, PercentagePoint } from './components/DataPoint';
import { formatDurationHours } from '../../constants/durationUtils';
import { fetchTotalDowntime } from '../../store/reporting/actions';
import { formatDateTimeForAPI } from '../../constants/utils';
import { UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';

const COLORS = {
  total: '#6b7280',
  unplanned: '#ef4444',
  planned: '#3b82f6',
};

const CustomTooltip = ({ active, payload }) => {
  const { t } = useTranslation();
  if (!active || !payload || !payload.length) return null;

  const dataPoint = payload[0]?.payload;
  if (!dataPoint) return null;

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
        fontSize: '0.85rem',
      }}
    >
      <div className="fw-bold mb-2">{dataPoint.periodLabel}</div>
      <div className="d-flex align-items-center gap-2 mb-1">
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: COLORS.planned,
            display: 'inline-block',
          }}
        />
        <span className="text-muted">{t('shared.planned_percentage', 'Planned %')}</span>
        <span className="fw-bold ms-auto">{dataPoint.plannedPercentage}%</span>
      </div>
      <div className="d-flex align-items-center gap-2 mb-1">
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '4px',
            backgroundColor: COLORS.total,
            display: 'inline-block',
          }}
        />
        <span className="text-muted">{t('shared.total', 'Total')}</span>
        <span className="fw-bold ms-auto">{formatDurationHours(dataPoint.totalDowntimeHours, { t, compact: true })}</span>
      </div>
      <div className="d-flex align-items-center gap-2 mb-1">
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '4px',
            backgroundColor: COLORS.unplanned,
            display: 'inline-block',
          }}
        />
        <span className="text-muted">{t('shared.unplanned', 'Unplanned')}</span>
        <span className="fw-bold ms-auto">{formatDurationHours(dataPoint.unplannedDowntimeHours, { t, compact: true })}</span>
      </div>
      <div className="d-flex align-items-center gap-2">
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '4px',
            backgroundColor: COLORS.planned,
            display: 'inline-block',
          }}
        />
        <span className="text-muted">{t('shared.planned', 'Planned')}</span>
        <span className="fw-bold ms-auto">{formatDurationHours(dataPoint.plannedDowntimeHours, { t, compact: true })}</span>
      </div>
    </div>
  );
};

/**
 * Self-fetching by default. When {@code data} is passed externally the
 * widget skips its Redux dispatch and renders the supplied payload —
 * used by the Global Dashboard to embed the same JSX cross-org.
 */
const TotalDowntimeWidget = ({
  organizationId, dateRange, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber,
  data: externalData, loading: externalLoading, error: externalError,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    totalDowntime: reduxData,
    totalDowntimeLoading: reduxLoading,
    totalDowntimeError: reduxError,
  } = useSelector(state => state.Reporting);
  const externalMode = externalData !== undefined;
  const data = externalMode ? externalData : reduxData;
  const loading = externalMode ? !!externalLoading : reduxLoading;
  const error = externalMode ? externalError : reduxError;

  // Legend hover state
  const [hoveredLegend, setHoveredLegend] = useState(null);

  // Fetch data when deps change (skipped in external mode).
  useEffect(() => {
    if (externalMode) return;
    if (!organizationId || !dateRange?.startDate || !dateRange?.endDate) return;
    dispatch(fetchTotalDowntime(
      organizationId,
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      criticality,
      locationId,
      vendorId,
      assetId,
      assetTypeId,
      contractNumber,
    ));
  }, [externalMode, dispatch, organizationId, dateRange?.startDate, dateRange?.endDate, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber]);

  const handleRefresh = useCallback(() => {
    if (externalMode) return;
    if (!organizationId || !dateRange?.startDate || !dateRange?.endDate) return;
    dispatch(fetchTotalDowntime(
      organizationId,
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      criticality,
      locationId,
      vendorId,
      assetId,
      assetTypeId,
      contractNumber,
    ));
  }, [dispatch, organizationId, dateRange, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber]);

  const summary = data?.summary;
  const chartData = data?.chartData || [];
  const isEmpty = !data || !chartData.length;
  const hasActiveFilters = criticality || locationId || vendorId || assetId || assetTypeId || contractNumber;

  const getOpacity = (dataKey, type) => {
    if (!hoveredLegend) return type === 'fill' ? 0.6 : 1;
    if (hoveredLegend === dataKey) return type === 'fill' ? 0.8 : 1;
    return type === 'fill' ? 0.15 : 0.2;
  };

  const handleLegendMouseEnter = (e) => {
    setHoveredLegend(e.dataKey);
  };

  const handleLegendMouseLeave = () => {
    setHoveredLegend(null);
  };

  return (
    <ReportWidget
      title={
        <span className="d-flex align-items-center gap-2">
          {t('reporting.total_downtime.title', 'Total Downtime')}
          <Info
            size={15}
            className="text-muted"
            id="totalDowntimeTooltip"
            style={{ cursor: 'help' }}
          />
          <UncontrolledTooltip placement="top" target="totalDowntimeTooltip">
            {t('reporting.total_downtime.tooltip', 'Shows total downtime hours over time, split by planned and unplanned. Helps identify downtime trends and evaluate whether planned maintenance is reducing unexpected failures.')}
          </UncontrolledTooltip>
        </span>
      }
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={hasActiveFilters
        ? t('reporting.total_downtime.empty_filtered', 'No downtime found matching the selected filters.')
        : t('reporting.total_downtime.empty', 'No downtime recorded in the selected period.')}
      onRefresh={externalMode ? undefined : handleRefresh}
    >
      {/* Summary Row */}
      {summary && (
        <div className="d-flex justify-content-center gap-5 mb-4 py-2">
          <DataPoint
            value={formatDurationHours(summary.totalDowntimeHours, { t, compact: true })}
            label={t('shared.total_hours', 'Total Hours')}
            color={COLORS.total}
          />
          <DataPoint
            value={formatDurationHours(summary.unplannedDowntimeHours, { t, compact: true })}
            label={t('shared.unplanned', 'Unplanned')}
            color={COLORS.unplanned}
          />
          <DataPoint
            value={formatDurationHours(summary.plannedDowntimeHours, { t, compact: true })}
            label={t('shared.planned', 'Planned')}
            color={COLORS.planned}
          />
          <PercentagePoint
            value={summary.plannedPercentage}
            label={t('shared.planned_percentage', 'Planned %')}
          />
        </div>
      )}

      {/* Area Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="periodLabel" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            onMouseEnter={handleLegendMouseEnter}
            onMouseLeave={handleLegendMouseLeave}
          />
          <Area
            type="monotone"
            dataKey="totalDowntimeHours"
            stroke={COLORS.total}
            fill={COLORS.total}
            fillOpacity={getOpacity('totalDowntimeHours', 'fill')}
            strokeOpacity={getOpacity('totalDowntimeHours', 'stroke')}
            name={t('shared.total', 'Total')}
          />
          <Area
            type="monotone"
            dataKey="unplannedDowntimeHours"
            stroke={COLORS.unplanned}
            fill={COLORS.unplanned}
            fillOpacity={getOpacity('unplannedDowntimeHours', 'fill')}
            strokeOpacity={getOpacity('unplannedDowntimeHours', 'stroke')}
            name={t('shared.unplanned', 'Unplanned')}
          />
          <Area
            type="monotone"
            dataKey="plannedDowntimeHours"
            stroke={COLORS.planned}
            fill={COLORS.planned}
            fillOpacity={getOpacity('plannedDowntimeHours', 'fill')}
            strokeOpacity={getOpacity('plannedDowntimeHours', 'stroke')}
            name={t('shared.planned', 'Planned')}
          />
        </AreaChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default TotalDowntimeWidget;
